import {
  type BaseFAQ,
  type FAQSection,
  getAllFaqCategories,
  getFaqSectionsByCategoryName,
} from '@bulknutrients/bulk-shared-lib/lib/sanity/faq';
import { getRedirect } from '@bulknutrients/bulk-shared-lib/lib/sanity/redirect';
import { Breadcrumbs, type Crumb } from '@components/Breadcrumbs/Breadcrumbs';
import { Container } from '@components/Container';
import { StandardLayout } from '@components/Layouts';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import type { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import type { ReactElement } from 'react';

export default function FaqsPage({
  categoryInfo,
  categorySections,
  breadcrumbs,
}: {
  categoryInfo: BaseFAQ;
  categorySections: FAQSection[];
  breadcrumbs: Crumb[];
}): ReactElement {
  return (
    <>
      <Head>
        <title>{`${categoryInfo.title} | Bulk Nutrients`}</title>
        <meta name="title" content={`${categoryInfo.title} | Bulk Nutrients`} />
        <meta
          name="description"
          content={`Find answers to common questions about Bulk Nutrients ${categoryInfo.title}.`}
        />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_SERVER_URL}${categoryInfo.slug}`}
        />
      </Head>
      <Container>
        <Breadcrumbs crumbs={breadcrumbs} />
      </Container>
      <Container
        as="main"
        id="main-content"
        className="mb-12.5 grid-cols-[1fr_min(var(--width-narrow-page),calc(100%-16px))_1fr]"
      >
        <h1>{categoryInfo.title}</h1>
        {categorySections.map((s) => (
          <div key={s.title} className="my-4">
            <h2 className="italic">{s.title}</h2>
            <ul className="list-disc pl-4">
              {s.questions.map((q) => (
                <li key={q.title} className="my-3">
                  <TextLink href={q.slug}>{q.title}</TextLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </Container>
    </>
  );
}

FaqsPage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};

export const getStaticPaths: GetStaticPaths = async () => {
  const categories = await getAllFaqCategories();

  return {
    paths: categories.map((c) => c.slug),
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const category = params?.category?.toString();
  if (!category) {
    return {
      notFound: true,
    };
  }

  const redirect = await getRedirect(
    `/faqs/${params?.category?.toString()}`.split(',').join('/') as string
  );

  if (redirect) {
    return {
      redirect: {
        destination: redirect.newUrl,
        permanent: redirect.permanent,
      },
    };
  }

  // faq category slug is formatted and won't match anything in sanity,
  // need to get title and slug from getFaqCategoryNames and match
  const categories = await getAllFaqCategories();
  const categoryInfo = categories.filter((c) => c.slug.includes(category))[0];
  const categorySections = await getFaqSectionsByCategoryName(
    categoryInfo.title
  );

  const breadcrumbs = [
    { title: 'home', path: '/' },
    { title: 'FAQs', path: '/faqs' },
  ];

  return {
    props: {
      categoryInfo,
      categorySections,
      breadcrumbs,
    },
  };
};
